/** @file One brush dab on a skinned mesh's weight rows: add, subtract, replace or smooth, with falloff. */
import { normalizeVertexWeights } from '@pose2d/commands/mesh/normalizeVertexWeights';
import type { Id } from '@pose2d/format/types';
import { brushFalloff } from './brushFalloff';
import { setShare } from './setShare';

type Influences = [Id, number][];

/** What the brush does to the paint bone's share (smooth ignores the bone and blends toward the neighbours). */
export type BrushMode = 'add' | 'subtract' | 'replace' | 'smooth';

/** Brush settings (the paintWeights brush store holds them; radius in screen px). */
export interface BrushSettings {
  mode: BrushMode;
  radiusPx: number;
  strength: number;
  hardness: number;
  /** Target share of the replace mode. */
  value: number;
  autoNormalize: boolean;
}

/** The mesh as painted: world positions at stroke start and triangle-edge neighbours (commands meshNeighbors). */
export interface PaintSurface {
  points: ArrayLike<number>;
  neighbors: readonly (readonly number[])[];
}

/** A dab centre and radius in world units. */
export interface Dab {
  x: number;
  y: number;
  radius: number;
}

const shareOf = (row: readonly (readonly [Id, number])[], bone: Id): number => row.find(([b]) => b === bone)?.[1] ?? 0;

/** The new share of the paint bone for strength a at this vertex. */
function nextShare(mode: Exclude<BrushMode, 'smooth'>, s: number, a: number, value: number): number {
  if (mode === 'add') return s + a * (1 - s);
  if (mode === 'subtract') return s * (1 - a);
  return s + a * (value - s);
}

/** (1 − a)·row + a·(mean of the neighbours' rows), normalized; the row itself when it has no neighbour. */
function smoothed(before: readonly Influences[], around: readonly number[], v: number, a: number): Influences {
  const row = before[v] ?? [];
  if (around.length === 0) return row;
  const mixed = new Map<Id, number>(row.map(([b, w]) => [b, (1 - a) * w]));
  for (const u of around) for (const [b, w] of before[u] ?? []) mixed.set(b, (mixed.get(b) ?? 0) + (a * w) / around.length);
  return normalizeVertexWeights([...mixed]) ?? row;
}

/** Applies one dab to `rows` (entries replaced, never mutated); returns how many vertices it touched. */
export function paintDab(rows: Influences[], surface: PaintSurface, dab: Dab, bone: Id, brush: BrushSettings): number {
  const before = brush.mode === 'smooth' ? rows.slice() : rows;
  let touched = 0;
  for (let v = 0; v < rows.length; v++) {
    const d = Math.hypot((surface.points[2 * v] ?? NaN) - dab.x, (surface.points[2 * v + 1] ?? NaN) - dab.y);
    const a = brush.strength * brushFalloff(d, dab.radius, brush.hardness);
    const row = rows[v];
    if (!(a > 0) || row === undefined) continue;
    rows[v] = brush.mode === 'smooth'
      ? smoothed(before, surface.neighbors[v] ?? [], v, a)
      : setShare(row, bone, nextShare(brush.mode, shareOf(row, bone), a, brush.value), brush.autoNormalize);
    touched++;
  }
  return touched;
}
